import { forwardRef, type Ref, useCallback, useEffect, useRef } from "react";
import type { TextInput } from "react-native";
import type { TamaguiElement } from "tamagui";
import { dateTextToIso, formatDate } from "../../utils/formatters";
import type { IconComponent } from "../Icon";
import { Input, type InputProps } from "../Input";
const DATE_MASK = "MM/DD/YYYY";
const DATE_MASK_LENGTH = DATE_MASK.length;

/** Sentinel so an initial `undefined` validity result still fires once. */
const NOT_YET_EMITTED = Symbol("not-yet-emitted");
export type DateFieldProps = Omit<InputProps, "value" | "defaultValue" | "onChangeText" | "onChange" | "type" | "inputMode" | "keyboardType" | "maxLength" | "placeholder" | "rightIcon" | "rightIconRef" | "rightIconLabel" | "onRightIconPress" | "clearable" | "onClear"> & {
  /** Masked MM/DD/YYYY text. Controlled — a passed value is re-masked, so pass
   *  masked text (`12/03/1990`), not ISO. `undefined` is empty. */
  value: string | undefined;
  /** The display channel: fires the raw masked text (e.g. `12/3`, including
   *  calendar-invalid input) on every keystroke. Drives the controlled `value`. */
  onValueChange?: (text: string) => void;
  /** The validity signal: fires an ISO `YYYY-MM-DD` (or `undefined` when
   *  incomplete/invalid) whenever the resolved value changes — typing *or* a
   *  programmatic `value` update. Deduplicated to the validity result. */
  onValidChange?: (iso: string | undefined) => void;
  /** Calendar-trigger icon for the reserved right slot (pairs with the picker). */
  calendarIcon?: IconComponent;
  onCalendarPress?: () => void;
  calendarLabel?: string;
  calendarButtonRef?: Ref<TamaguiElement>;
};
export const DateField = forwardRef<TextInput | null, DateFieldProps>(function DateField({
  value,
  onValueChange,
  onValidChange,
  calendarIcon,
  onCalendarPress,
  calendarLabel = "Open calendar",
  calendarButtonRef,
  ...inputProps
}, forwardedRef) {
  // Re-mask the controlled value so digit-only input ("12031990") still gets
  // separators. Value is masked text, never ISO (see the `value` prop doc).
  const masked = value === undefined ? "" : formatDate(value);

  // Dedupe to the validity result so incomplete input doesn't spam `undefined`.
  const lastValidRef = useRef<string | undefined | typeof NOT_YET_EMITTED>(NOT_YET_EMITTED);
  useEffect(() => {
    const iso = dateTextToIso(masked);
    if (iso !== lastValidRef.current) {
      lastValidRef.current = iso;
      onValidChange?.(iso);
    }
  }, [masked, onValidChange]);
  const handleChangeText = useCallback((text: string) => {
    onValueChange?.(formatDate(text));
  }, [onValueChange]);
  const calendarTrigger = calendarIcon && onCalendarPress ? {
    rightIcon: calendarIcon,
    rightIconLabel: calendarLabel,
    rightIconRef: calendarButtonRef,
    onRightIconPress: onCalendarPress
  } : {};
  return <Input ref={forwardedRef} type="text" inputMode="numeric" keyboardType="number-pad" maxLength={DATE_MASK_LENGTH} placeholder={DATE_MASK} value={masked} onChangeText={handleChangeText} {...calendarTrigger} {...inputProps} />;
});